import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Alarm, User } from '../types/alarm';
import { getDayName } from '../utils/alarmUtils';
import { checkAlarmCategoryAccess } from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';

interface AlarmBlockProps {
  alarm: Alarm;
  onUpdate: (alarm: Alarm) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  onCopy: (alarm: Alarm, targetDay: number) => void;
  formatAlarmTime: (time: string) => string | React.ReactElement;
  timeFormat: '12h' | '24h';
  isSetActive: boolean; // 추가
  isExpanded: boolean; // 단일 확장 관리용
  onToggle: (alarmId: string) => void; // 확장/축소 토글 함수
  user?: User;
  onLicenseClick?: () => void;
}

const BlockContainer = styled.div<{ $isExpanded: boolean }>`
  background: #ffffff;

  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
  border: 2px solid var(--color-border); // 항상 회색 테두리 사용
  border-radius: 12px;
  padding: 16px;
  margin: 4px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const TimeDisplay = styled.div<{ $isInitial: boolean }>`
  font-size: 2.3rem;
  font-weight: bold;
  color: var(--color-text-primary);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

const TimeContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

// 알람 카테고리 표시 (왼쪽) - 초록색 #10504A
const AlarmCategoryDisplay = styled.div<{ $isExpanded?: boolean }>`
  display: ${props => props.$isExpanded ? 'none' : 'block'};
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.75rem;
  background: #10504A;
  color: white;
  text-align: center;
  width: fit-content;
  white-space: nowrap;
  position: absolute;
  left: 0;
  
  /* 다크모드: #404040 */
  :root:not([data-theme='light']) & {
    background: #404040;
  }
`;

// 알람 타입 표시 (오른쪽) - 오전/오후 텍스트와 같은 색상
const AlarmTypeDisplay = styled.div<{ $isExpanded?: boolean }>`
  display: ${props => props.$isExpanded ? 'none' : 'block'};
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.75rem;
  font-family: 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #bbb;
  color: white;
  text-align: center;
  width: fit-content;
  white-space: nowrap;
  position: absolute;
  right: 0;
  
  /* 다크모드: #404040 */
  :root:not([data-theme='light']) & {
    background: #404040;
  }
`;

const AmPmToggleInline = styled.button<{ $isAm: boolean }>`
  background: #bbb;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 4px 8px;
  min-width: 44px;
  min-height: 28px;
  font-size: 0.8rem;
  font-weight: 800;
  font-family: 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: background 0.3s;
  outline: none;
  white-space: nowrap;
  
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(73, 80, 87, 0.3);
  }
`;

const HourInput = styled.input`
  font-size: 2.8rem;
  font-weight: bold;
  color: var(--color-text-primary);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  border-radius: 8px;
  width: 60px;
  cursor: text;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  
  &:focus {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #ccc;
  }
`;

const MinuteInput = styled.input`
  font-size: 2.8rem;
  font-weight: bold;
  color: var(--color-text-primary);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  border-radius: 8px;
  width: 60px;
  cursor: text;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  
  &:focus {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #ccc;
  }
`;

const TimeSeparator = styled.span`
  font-size: 2.8rem;
  font-weight: bold;
  color: var(--color-text-primary);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  margin: 0 1px;
`;


const ClockButton = styled.button`
  background: transparent;
  color: var(--color-text-gray);
  border: none;
  padding: 8px;
  cursor: pointer;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: transparent;
    color: var(--color-text-primary);
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const AlarmRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const TimeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  position: relative;
`;

const TimeEditInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const AlarmNameInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1.2rem;
  margin-bottom: 12px;
  text-align: center;
  font-weight: bold;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const TimeEditGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const ExpandedContent = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
`;

const CategorySelector = styled.div`
  margin-bottom: 16px;
`;

const CategoryLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const CategorySelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CategorySelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const CategoryOption = styled.div`
  flex: 1;
  text-align: center;
`;

const CategoryDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const CategoryDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: hidden;
`;

const CategoryDropdownOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 1rem;
  color: var(--color-text-primary);
  text-align: center;
  
  &:hover {
    background-color: var(--color-panel);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const TypeButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  flex-shrink: 1;
  min-width: 0;
  min-height: 36px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: clamp(0.7rem, 2vw, 0.9rem);
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  
  svg {
    color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
    transition: color 0.2s ease;
    flex-shrink: 0;
  }
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    
    svg {
      color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
    }
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    min-height: 32px;
    font-size: clamp(0.65rem, 2vw, 0.85rem);
  }
`;



const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const SaveButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  flex: 1;
  font-size: 1rem;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const CancelButton = styled.button`
  background: #bbb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;
  
  &:hover {
    background: #999;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  color: var(--color-text-gray);
  border: none;
  border-radius: 6px;
  padding: 8px;
  min-width: 40px;
  min-height: 40px;
  cursor: pointer;
  flex: 1;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  
  &:hover {
    background: transparent;
    color: var(--color-text-primary);
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const CopyButtonInline = styled.button`
  background: transparent;
  color: var(--color-text-gray);
  border: none;
  border-radius: 6px;
  padding: 8px;
  min-width: 40px;
  min-height: 40px;
  cursor: pointer;
  flex: 1;
  margin-top: 8px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  
  &:hover {
    background: transparent;
    color: var(--color-text-primary);
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const BottomButtonRow = styled.div`
  display: flex;
  width: 100%;
  margin-top: 8px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow);
`;

const ModalTitle = styled.h3`
  margin-bottom: 16px;
  color: var(--color-text-primary);
`;

const ModalMessage = styled.p`
  margin: 0 0 20px 0;
  font-size: 1rem;
  color: var(--color-text-gray);
  text-align: center;
  line-height: 1.5;
`;

const DaySelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const DayButton = styled.button<{ $isSelected: boolean }>`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.$isSelected ? 'transparent' : 'var(--color-border)'} !important;
  border-radius: 50%;
  background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$isSelected ? 'white' : 'var(--color-text-primary)'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'} !important;
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }
  
  &:hover {
    background: ${props => props.$isSelected ? 'var(--color-accent)' : 'var(--color-surface-highlight)'};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $isPrimary?: boolean; disabled?: boolean }>`
  background: ${props => {
    if (props.disabled) {
      return 'var(--color-panel)';
    }
    return props.$isPrimary ? 'var(--color-accent-strong)' : '#bbb';
  }};
  color: ${props => {
    if (props.disabled) {
      return '#999';
    }
    return 'white';
  }};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => {
      if (props.disabled) {
        return 'var(--color-panel)';
      }
      return props.$isPrimary ? 'var(--color-accent)' : '#999';
    }};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const OffsetInfo = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-gray);
  text-align: center;
  margin-top: 8px;
`;

const TimePickerModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const TimePickerContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 300px;
  width: 90%;
  text-align: center;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow);
`;

const TimePickerTitle = styled.h3`
  margin-bottom: 16px;
  color: var(--color-text-primary);
`;

const TimePickerRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
`;

const TimePickerColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimePickerLabel = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-gray);
  margin-bottom: 8px;
`;

const TimePickerSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1.1rem;
  background: var(--color-surface);
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;





const AlarmBlock: React.FC<AlarmBlockProps> = ({ 
  alarm, 
  onUpdate, 
  onToggleActive, 
  onDelete, 
  onCopy, 
  formatAlarmTime, 
  timeFormat,
  isSetActive,
  isExpanded,
  onToggle,
  user,
  onLicenseClick
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedCopyDays, setSelectedCopyDays] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [isAm, setIsAm] = useState(() => {
    const [h] = alarm.time.split(':').map(Number);
    return h < 12;
  });
  const [tempSettings, setTempSettings] = useState({
    time: alarm.time,
    type: alarm.type,
    name: alarm.name || '',
    days: [...alarm.days],
    category: alarm.category || 'basic' as 'basic' | 'class'
  });
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [hourInput, setHourInput] = useState('');
  const [minuteInput, setMinuteInput] = useState('');

  // 초기값 설정
  React.useEffect(() => {
    if (isExpanded) {
      if (timeFormat === '12h') {
        const [h] = tempSettings.time.split(':').map(Number);
        const hour12 = h % 12 || 12;
        setHourInput(hour12.toString());
      } else {
        setHourInput(tempSettings.time.split(':')[0]);
      }
      setMinuteInput(tempSettings.time.split(':')[1]);
    }
  }, [isExpanded, tempSettings.time, timeFormat]);
  
  const blockRef = useRef<HTMLDivElement>(null);


  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStartX;
    if (deltaX < -50) { // 왼쪽으로 50px 이상 드래그
      onToggleActive(alarm.id);
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSettingsClick = () => {
    // 확장된 상태에서는 클릭해도 닫히지 않음 (닫기 버튼으로만 닫기 가능)
    if (!isExpanded) {
      onToggle(alarm.id);
    }
  };

  const handleSave = () => {
    // 카테고리 접근 권한 체크
    if (user) {
      const accessCheck = checkAlarmCategoryAccess(user, tempSettings.category);
      if (!accessCheck.allowed) {
        setLicenseLimitMessage(accessCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    
    onUpdate({
      ...alarm,
      ...tempSettings,
      category: tempSettings.category
    });
    onToggle(alarm.id); // 저장 후 확장 상태 닫기
  };

  const handleCategoryChange = (category: 'basic' | 'class') => {
    // 카테고리 변경 시 권한 체크
    if (user) {
      const accessCheck = checkAlarmCategoryAccess(user, category);
      if (!accessCheck.allowed) {
        setLicenseLimitMessage(accessCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    setTempSettings(prev => ({ ...prev, category }));
  };

  const handleCancel = () => {
    setTempSettings({
      time: alarm.time,
      type: alarm.type,
      name: alarm.name || '',
      days: [...alarm.days],
      category: alarm.category || 'basic' as 'basic' | 'class'
    });
    setShowLicenseLimitModal(false);
    setLicenseLimitMessage('');
    setIsCategoryDropdownOpen(false);
    onToggle(alarm.id); // 취소 후 확장 상태 닫기
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(alarm.id);
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleCopyClick = () => {
    setShowCopyModal(true);
    setSelectedCopyDays([]);
  };

  const handleCopyConfirm = () => {
    if (selectedCopyDays.length > 0) {
      selectedCopyDays.forEach(day => {
        onCopy(alarm, day);
      });
      setShowCopyModal(false);
    }
  };

  const handleCopyCancel = () => {
    setShowCopyModal(false);
    setSelectedCopyDays([]);
  };

  const toggleCopyDay = (dayIndex: number) => {
    setSelectedCopyDays(prev => 
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex].sort()
    );
  };

  const getTypeDisplay = (type: '1M' | '3M' | '5M') => {
    switch (type) {
      case '1M': return '1분 전';
      case '3M': return '3분 전';
      case '5M': return '5분 전';
      default: return '1분 전';
    }
  };

  const getCategoryDisplay = (category?: 'basic' | 'class') => {
    return category === 'class' ? '수업' : '기본';
  };

  const isInitial = alarm.time === '00:00';

  // 시간 입력값을 12시간제로 변환 (오전/오후 텍스트 없이)
  const get12HourTime = () => {
    let [h, m] = tempSettings.time.split(':').map(Number);
    let hour = h % 12;
    if (hour === 0) hour = 12;
    return `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // 현재 시간 형식에 맞는 시간 표시 함수
  const getDisplayTime = () => {
    if (timeFormat === '12h') {
      return get12HourTime();
    } else {
      return tempSettings.time;
    }
  };

  // 오전/오후 토글 핸들러
  const handleAmPmToggle = () => {
    let [h, m] = tempSettings.time.split(':').map(Number);
    if (isAm) {
      // 오전 -> 오후
      if (h < 12) h += 12;
    } else {
      // 오후 -> 오전
      if (h >= 12) h -= 12;
    }
    setTempSettings(prev => ({ ...prev, time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}` }));
    setIsAm(!isAm);
  };

  // 시계 아이콘 클릭 핸들러
  const handleClockClick = () => {
    setShowTimePicker(true);
  };

  // 시간 선택 모달에서 시간 변경 핸들러
  const handleTimePickerChange = (type: 'hour' | 'minute', value: string) => {
    let [h, m] = tempSettings.time.split(':').map(Number);
    
    if (type === 'hour') {
      let hour = parseInt(value);
      if (timeFormat === '12h') {
        if (isAm && hour === 12) hour = 0;
        if (!isAm && hour < 12) hour += 12;
      }
      h = hour;
    } else if (type === 'minute') {
      m = parseInt(value);
    }
    
    setTempSettings(prev => ({ 
      ...prev, 
      time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}` 
    }));
  };

  // 시간 선택 모달 닫기
  const handleTimePickerClose = () => {
    setShowTimePicker(false);
  };

  // 시간 입력 핸들러
  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // 빈 문자열이거나 숫자가 아닌 경우 처리
    if (value === '' || !/^\d*$/.test(value)) {
      setHourInput(value);
      return;
    }
    
    setHourInput(value);
    
    const [, m] = tempSettings.time.split(':').map(Number);
    
    if (timeFormat === '12h') {
      let hour = parseInt(value);
      if (isNaN(hour)) return;
      
      // 12를 넘는 숫자를 입력했을 때만 토글을 오후로 변경
      if (hour > 12) {
        if (isAm) setIsAm(false); // 오후로 변경
      }
      
      // 12시간 형식을 24시간 형식으로 변환
      let newHour = hour;
      if (!isAm && hour < 12) {
        newHour = hour + 12;
      } else if (isAm && hour === 12) {
        newHour = 0;
      }
      
      const newTime = `${newHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      setTempSettings(prev => ({ ...prev, time: newTime }));
    } else {
      // 24시간 형식에서는 입력값이 2자리이고 유효한 시간인 경우에만 tempSettings 업데이트
      if (value.length === 2) {
        let hour = parseInt(value);
        if (!isNaN(hour) && hour >= 0 && hour <= 23) {
          const newTime = `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
          setTempSettings(prev => ({ ...prev, time: newTime }));
        }
      }
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // 빈 문자열이거나 숫자가 아닌 경우 처리
    if (value === '' || !/^\d*$/.test(value)) {
      setMinuteInput(value);
      return;
    }
    
    setMinuteInput(value);
    
    // 입력값이 2자리이고 유효한 분인 경우에만 tempSettings 업데이트
    if (value.length === 2) {
      const minute = parseInt(value);
      if (!isNaN(minute) && minute >= 0 && minute <= 59) {
        const [h] = tempSettings.time.split(':').map(Number);
        const newTime = `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        setTempSettings(prev => ({ ...prev, time: newTime }));
      }
    }
  };

  // 시간 입력 blur 핸들러
  const handleHourBlur = () => {
    if (timeFormat === '12h') {
      const hour = parseInt(hourInput);
      if (!isNaN(hour) && hour > 12) {
        // 12를 넘는 숫자를 1-12 범위로 변환
        const normalizedHour = hour % 12 || 12;
        setHourInput(normalizedHour.toString());
        
        // 24시간 형식으로 변환하여 tempSettings 업데이트
        const [, m] = tempSettings.time.split(':').map(Number);
        let newHour = normalizedHour;
        if (!isAm && normalizedHour < 12) {
          newHour = normalizedHour + 12;
        } else if (isAm && normalizedHour === 12) {
          newHour = 0;
        }
        
        const newTime = `${newHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        setTempSettings(prev => ({ ...prev, time: newTime }));
      }
    } else {
      // 24시간 형식에서 blur 시 포맷팅
      const hour = parseInt(hourInput);
      if (!isNaN(hour) && hour >= 0 && hour <= 23) {
        // 시간을 2자리로 포맷팅
        setHourInput(hour.toString().padStart(2, '0'));
        
        const [, m] = tempSettings.time.split(':').map(Number);
        const newTime = `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        setTempSettings(prev => ({ ...prev, time: newTime }));
      }
    }
  };

  const handleMinuteBlur = () => {
    const minute = parseInt(minuteInput);
    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      // 분을 2자리로 포맷팅
      setMinuteInput(minute.toString().padStart(2, '0'));
      
      const [h] = tempSettings.time.split(':').map(Number);
      const newTime = `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      setTempSettings(prev => ({ ...prev, time: newTime }));
    }
  };



  return (
    <>
      <BlockContainer
        ref={blockRef}
        $isExpanded={isExpanded}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleSettingsClick}
      >
        <AlarmRow>
          <LeftSection>
          </LeftSection>
          <CenterSection>
            <TimeSection>
              <TimeRow>
                <AlarmCategoryDisplay $isExpanded={isExpanded}>
                  {getCategoryDisplay(alarm.category)}
                </AlarmCategoryDisplay>
                <TimeContainer>
                  {isExpanded ? (
                    <TimeEditGroup>
                      {timeFormat === '12h' && (
                        <AmPmToggleInline $isAm={isAm} onClick={(e) => { e.stopPropagation(); handleAmPmToggle(); }}>
                          {isAm ? '오전' : '오후'}
                        </AmPmToggleInline>
                      )}
                      <TimeEditInputs>
                        <HourInput
                          type="text"
                          value={hourInput}
                          onChange={handleHourChange}
                          onBlur={handleHourBlur}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                        />
                        <TimeSeparator>:</TimeSeparator>
                        <MinuteInput
                          type="text"
                          value={minuteInput}
                          onChange={handleMinuteChange}
                          onBlur={handleMinuteBlur}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                        />
                      </TimeEditInputs>
                      <ClockButton onClick={(e) => { e.stopPropagation(); handleClockClick(); }}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ClockButton>
                    </TimeEditGroup>
                  ) : (
                    <TimeDisplay $isInitial={isInitial}>
                      {formatAlarmTime(alarm.time)}
                    </TimeDisplay>
                  )}
                </TimeContainer>
                <AlarmTypeDisplay $isExpanded={isExpanded}>
                  {getTypeDisplay(alarm.type)}
                </AlarmTypeDisplay>
              </TimeRow>
            </TimeSection>
          </CenterSection>
          <RightSection>
          </RightSection>
        </AlarmRow>
      
      <OffsetInfo>
        {isExpanded ? (
          <AlarmNameInput
            value={tempSettings.name}
            onChange={(e) => setTempSettings(prev => ({ ...prev, name: e.target.value }))}
            placeholder="알람 이름을 입력하세요"
          />
        ) : (
          alarm.name || '알람'
        )}
      </OffsetInfo>

              {isExpanded && (
        <ExpandedContent>
          <CategorySelector>
            <CategoryLabel>알람 종류</CategoryLabel>
            <CategorySelectContainer>
              <CategorySelectButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                }}
              >
                <CategoryOption>
                  {tempSettings.category === 'basic' ? '기본알람' : '수업알람'}
                </CategoryOption>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </CategorySelectButton>
              
              {isCategoryDropdownOpen && (
                <>
                  <CategoryDropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                  <CategoryDropdown>
                    <CategoryDropdownOption
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryChange('basic');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span>기본알람</span>
                    </CategoryDropdownOption>
                    <CategoryDropdownOption
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryChange('class');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span>수업알람</span>
                    </CategoryDropdownOption>
                  </CategoryDropdown>
                </>
              )}
            </CategorySelectContainer>
          </CategorySelector>
          <TypeSelector>
            {(['1M', '3M', '5M'] as const).map(type => (
              <TypeButton
                key={type}
                type="button"
                $isSelected={tempSettings.type === type}
                onClick={(e) => {
                  e.stopPropagation();
                  setTempSettings(prev => ({ ...prev, type }));
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: '8px', flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {getTypeDisplay(type)}
              </TypeButton>
            ))}
          </TypeSelector>
          <ButtonGroup>
            <CancelButton data-variant="modal-secondary" onClick={(e) => { e.stopPropagation(); handleCancel(); }}>닫기</CancelButton>
            <SaveButton onClick={(e) => { e.stopPropagation(); handleSave(); }}>저장</SaveButton>
          </ButtonGroup>
          <BottomButtonRow>
            <DeleteButton onClick={(e) => { e.stopPropagation(); handleDelete(); }}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </DeleteButton>
            <CopyButtonInline onClick={(e) => { e.stopPropagation(); handleCopyClick(); }}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CopyButtonInline>
          </BottomButtonRow>
        </ExpandedContent>
      )}
      </BlockContainer>

      {/* Portal을 사용하여 팝업들을 페이지 전체에 렌더링 */}
      {showCopyModal && createPortal(
        <Modal onClick={handleCopyCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>복사할 요일을 선택하세요</ModalTitle>
            <DaySelector>
              {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                <DayButton
                  key={dayIndex}
                  $isSelected={selectedCopyDays.includes(dayIndex)}
                  onClick={() => toggleCopyDay(dayIndex)}
                >
                  {getDayName(dayIndex).charAt(0)}
                </DayButton>
              ))}
            </DaySelector>
            <ModalButtonGroup>
              <ModalButton data-variant="modal-secondary" onClick={handleCopyCancel}>
                닫기
              </ModalButton>
              <ModalButton $isPrimary onClick={handleCopyConfirm} disabled={selectedCopyDays.length === 0}>
                복사
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </Modal>,
        document.body
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && createPortal(
        <Modal onClick={handleDeleteCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>알람 삭제</ModalTitle>
            <ModalMessage>이 알람을 삭제하시겠습니까?</ModalMessage>
            <ModalButtonGroup>
              <ModalButton data-variant="modal-secondary" onClick={handleDeleteCancel}>
                취소
              </ModalButton>
              <ModalButton $isPrimary onClick={handleDeleteConfirm}>
                삭제
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </Modal>,
        document.body
      )}

      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />

      {showTimePicker && createPortal(
        <TimePickerModal onClick={handleTimePickerClose}>
          <TimePickerContent onClick={(e) => e.stopPropagation()}>
            <TimePickerTitle>시간 선택</TimePickerTitle>
            <TimePickerRow>
              {timeFormat === '12h' && (
                <TimePickerColumn>
                  <TimePickerLabel>오전/오후</TimePickerLabel>
                  <TimePickerSelect
                    value={isAm ? 'am' : 'pm'}
                    onChange={(e) => {
                      const newIsAm = e.target.value === 'am';
                      setIsAm(newIsAm);
                      
                      // 시간도 함께 업데이트
                      let [h, m] = tempSettings.time.split(':').map(Number);
                      if (newIsAm && h >= 12) {
                        h -= 12;
                      } else if (!newIsAm && h < 12) {
                        h += 12;
                      }
                      setTempSettings(prev => ({ 
                        ...prev, 
                        time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}` 
                      }));
                    }}
                  >
                    <option value="am">오전</option>
                    <option value="pm">오후</option>
                  </TimePickerSelect>
                </TimePickerColumn>
              )}
              <TimePickerColumn>
                <TimePickerLabel>시간</TimePickerLabel>
                <TimePickerSelect
                  value={getDisplayTime().split(':')[0]}
                  onChange={(e) => handleTimePickerChange('hour', e.target.value)}
                >
                  {Array.from({length: timeFormat === '12h' ? 12 : 24}, (_, i) => {
                    const hour = timeFormat === '12h' ? i + 1 : i;
                    return (
                      <option key={hour} value={hour.toString().padStart(2, '0')}>
                        {hour.toString().padStart(2, '0')}
                      </option>
                    );
                  })}
                </TimePickerSelect>
              </TimePickerColumn>
              <TimePickerColumn>
                <TimePickerLabel>분</TimePickerLabel>
                <TimePickerSelect
                  value={getDisplayTime().split(':')[1]}
                  onChange={(e) => handleTimePickerChange('minute', e.target.value)}
                >
                  {Array.from({length: 60}, (_, i) => i).map(minute => (
                    <option key={minute} value={minute.toString().padStart(2, '0')}>
                      {minute.toString().padStart(2, '0')}
                    </option>
                  ))}
                </TimePickerSelect>
              </TimePickerColumn>
            </TimePickerRow>
            <ModalButtonGroup>
              <ModalButton $isPrimary onClick={handleTimePickerClose}>
                확인
              </ModalButton>
            </ModalButtonGroup>
          </TimePickerContent>
        </TimePickerModal>,
        document.body
      )}

    </>
  );
};

export default AlarmBlock; 